import { useState, useEffect } from 'react';
import api from '../services/api';
import { useI18n } from '../i18n';

const ABSENCE_BADGE = {
  KRANK_MIT_ATTEST: 'bg-danger',
  KRANK_OHNE_ATTEST: 'bg-warning text-dark',
  FZT: 'badge-fzt',
};

export default function MonthView() {
  const { t, locale } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/time/summary/month/${year}/${month}`),
      api.get(`/absences/?year=${year}`),
    ])
      .then(([r, absR]) => {
        setData(r.data);
        const monthAbsences = absR.data.filter((a) => {
          const start = new Date(a.start_date);
          const end = new Date(a.end_date);
          return (
            (start.getFullYear() === year && start.getMonth() + 1 === month) ||
            (end.getFullYear() === year && end.getMonth() + 1 === month)
          );
        });
        setAbsences(monthAbsences);
      })
      .finally(() => setLoading(false));
  }, [year, month]);

  const formatMins = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };

  const monthNames = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(locale, { month: 'long' }));

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/reports/preview/${year}/${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = t('month.pdfFilename', { year, month: String(month).padStart(2, '0') });
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
        <h4 className="mb-0"><i className="bi bi-calendar3 me-2" />{t('month.title')}</h4>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <select className="form-select form-select-sm" value={month} onChange={(e) => setMonth(+e.target.value)}>
            {monthNames.map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
          </select>
          <select className="form-select form-select-sm" value={year} onChange={(e) => setYear(+e.target.value)}>
            {[now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1].map(y => <option key={y}>{y}</option>)}
          </select>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            title={t('month.pdfTitle')}
          >
            <i className="bi bi-file-earmark-pdf me-1" />
            {pdfLoading ? t('month.pdfLoading') : 'PDF'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border" /></div>
      ) : data ? (
        <>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-primary">{formatMins(data.total_worked_minutes)}</div>
                  <div className="text-muted small">{t('month.totalWorked')}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-success">{data.total_days}</div>
                  <div className="text-muted small">{t('month.workDays')}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-center">
                <div className="card-body">
                  <div className="fs-4 fw-bold text-info">
                    {data.total_days > 0 ? formatMins(Math.round(data.total_worked_minutes / data.total_days)) : '–'}
                  </div>
                  <div className="text-muted small">{t('month.avgPerDay')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>{t('month.colDate')}</th>
                    <th>{t('month.colIn')}</th>
                    <th>{t('month.colOut')}</th>
                    <th>{t('month.colBreak')}</th>
                    <th>{t('month.colWorked')}</th>
                    <th>{t('month.colLocation')}</th>
                    <th>{t('month.colNotes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.length === 0 ? (
                    <tr><td colSpan={7} className="text-center text-muted py-4">{t('time.noEntries')}</td></tr>
                  ) : data.days.map((d) => (
                    <tr key={d.date}>
                      <td>{new Date(d.date).toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' })}</td>
                      <td>{d.check_in ? new Date(d.check_in).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '–'}</td>
                      <td>{d.check_out ? new Date(d.check_out).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : <span className="badge bg-success">{t('time.active')}</span>}</td>
                      <td>{t('time.minutes', { n: d.break_minutes })}</td>
                      <td><strong>{formatMins(d.worked_minutes)}</strong></td>
                      <td>
                        {d.work_location === 'HOME_OFFICE'
                          ? <span className="badge bg-info"><i className="bi bi-house me-1" />{t('month.homeOfficeShort')}</span>
                          : <span className="badge bg-secondary"><i className="bi bi-building me-1" />{t('time.office')}</span>}
                      </td>
                      <td><small className="text-muted">{d.notes || ''}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {absences.length > 0 && (
            <div className="card mt-4">
              <div className="card-header">
                <i className="bi bi-bandaid me-2" /><strong>{t('month.absencesTitle')}</strong>
              </div>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>{t('time.absence.type')}</th>
                      <th>{t('time.absence.from')}</th>
                      <th>{t('time.absence.to')}</th>
                      <th>{t('time.absence.workDays')}</th>
                      <th>{t('time.absence.notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <span className={`badge ${ABSENCE_BADGE[a.absence_type]}`}>
                            {t(`time.absence.${a.absence_type}`)}
                          </span>
                        </td>
                        <td>{a.start_date}</td>
                        <td>{a.end_date}</td>
                        <td>{a.days_count}</td>
                        <td>{a.notes || '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
